'use client';

import { useState } from 'react';
import manifest from '@/assets/landing-screenshots.json';

/**
 * A product screenshot, served straight from GCS (see publish-landing-screenshots.cjs).
 * Nothing is bundled with the app, so the app server spends no storage or bandwidth on
 * these. Width/height come from the manifest so the page never shifts while they load,
 * and an image that fails to load simply leaves the frame's neutral background.
 */
export function Shot({ name, alt, priority = false, className = '' }) {
  const entry = manifest.screenshots[name];
  const [failed, setFailed] = useState(false);
  if (!entry || failed) return null;

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      className={className}
      src={entry.url}
      width={entry.width}
      height={entry.height}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Phone chrome around a phone-sized screenshot. The status bar is drawn in the app's
 * header colour so it reads as one continuous top bar above the real screen.
 */
export function PhoneFrame({ children, className = '' }) {
  return (
    <div className={`pframe ${className}`}>
      <span className="pframe-key k1" aria-hidden="true" />
      <span className="pframe-key k2" aria-hidden="true" />
      <div className="pframe-screen">
        <div className="pframe-status" aria-hidden="true">
          <b>9:41</b>
          <i className="island" />
          <span className="sys">
            <svg viewBox="0 0 18 12" width="16" height="11" focusable="false"><path d="M1 9h2v2H1zM5 6h2v5H5zM9 3h2v8H9zM13 0h2v11h-2z" fill="currentColor" /></svg>
            <svg viewBox="0 0 26 12" width="24" height="11" focusable="false"><rect x=".5" y=".5" width="21" height="11" rx="3" fill="none" stroke="currentColor" opacity=".6" /><rect x="2" y="2" width="15" height="8" rx="1.6" fill="currentColor" /><rect x="23" y="4" width="2" height="4" rx="1" fill="currentColor" opacity=".6" /></svg>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Browser-window chrome around a screenshot. */
export function BrowserFrame({ url = 'dashboard', children, className = '' }) {
  return (
    <div className={`bframe ${className}`}>
      <div className="bframe-bar" aria-hidden="true">
        <span className="dots"><i /><i /><i /></span>
        <span className="addr">
          <svg viewBox="0 0 16 16" width="11" height="11" focusable="false"><path d="M5 7V5a3 3 0 0 1 6 0v2M4 7h8v6H4z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
          vidyalayhelp.com/{url}
        </span>
      </div>
      <div className="bframe-body">{children}</div>
    </div>
  );
}
